import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Table,
  Tag,
  Input,
  DatePicker,
  Select,
  Empty,
  Spin,
  Popconfirm,
  Modal,
  Form,
  message,
  Progress,
} from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import {
  getAdminUsageHistory,
  addUsageToAdmin,
  updateUsageOfAdmin,
  deleteUsageRecords,
  getAdminUsageById,
} from "@api/user/superAdmin";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminUsageHistory = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [usageHistory, setUsageHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [planFilter, setPlanFilter] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchUsageHistory();
  }, []);

  const fetchUsageHistory = async () => {
    setLoading(true);
    try {
      const { data } = await getAdminUsageHistory(id);
      const enhancedData = (data || []).map((item, index) => ({
        ...item,
        key: item._id || `usage-${index}`,
      }));
      setUsageHistory(enhancedData);
      setFilteredHistory(enhancedData);
    } catch (error) {
      console.error("Error fetching usage history:", error);
      message.error("Failed to load usage history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("MMM DD, YYYY");
  };

  const calculatePercentage = (used, total) => {
    if (!total) return 0;
    return Math.min(Math.round((used / total) * 100), 100);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(value, planFilter);
  };

  const handlePlanFilterChange = (value) => {
    setPlanFilter(value);
    applyFilters(searchText, value);
  };

  const applyFilters = (search, plan) => {
    let filtered = [...usageHistory];
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((usage) =>
        usage.subscriptionPlan?.toLowerCase().includes(searchLower)
      );
    }
    if (plan) {
      filtered = filtered.filter((usage) => usage.subscriptionPlan === plan);
    }
    setFilteredHistory(filtered);
  };

  const resetFilters = () => {
    setSearchText("");
    setPlanFilter(null);
    setFilteredHistory(usageHistory);
  };

  // Add Modal
  const showAddUsageModal = () => {
    form.resetFields();
    form.setFieldsValue({
      plan: "Free",
      dateRange: [moment(), moment().add(1, "month")],
      creditsAllotted: 1000,
      billInterval: "month",
    });
    setAddModalVisible(true);
  };

  const handleAddUsage = async (values) => {
    setLoading(true);
    try {
      const payload = {
        plan: values.plan,
        startDate: values.dateRange[0].format("YYYY-MM-DD"),
        endDate: values.dateRange[1].format("YYYY-MM-DD"),
        creditsAllotted: Number(values.creditsAllotted) || 0,
        billInterval: values.billInterval,
      };
      await addUsageToAdmin(id, payload);
      message.success("Usage allocation added successfully");
      setAddModalVisible(false);
      fetchUsageHistory();
    } catch (error) {
      console.error("Error adding usage:", error);
      message.error("Failed to add usage allocation");
    } finally {
      setLoading(false);
    }
  };

  // Edit Modal
  const showEditUsageModal = async (usageId) => {
    setLoading(true);
    try {
      setEditId(usageId);
      const { data } = await getAdminUsageById(usageId);
      editForm.setFieldsValue({
        plan: data.subscriptionPlan,
        dateRange: [
          moment(data.subscriptionStartDate),
          moment(data.subscriptionEndDate),
        ],
        creditsAllotted: data.creditsAllotted || 0,
        creditsUsed: data.creditsUsed || 0,
        billInterval: data.billInterval || "month",
      });
      setEditModalVisible(true);
    } catch (error) {
      console.error("Error fetching usage details:", error);
      message.error("Failed to load usage details");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUsage = async (values) => {
    if (!editId) return;
    setLoading(true);
    try {
      const payload = {
        plan: values.plan,
        startDate: values.dateRange[0].format("YYYY-MM-DD"),
        endDate: values.dateRange[1].format("YYYY-MM-DD"),
        creditsAllotted: Number(values.creditsAllotted) || 0,
        creditsUsed: Number(values.creditsUsed) || 0,
        billInterval: values.billInterval,
      };
      await updateUsageOfAdmin(id, editId, payload);
      message.success("Usage allocation updated successfully");
      setEditModalVisible(false);
      fetchUsageHistory();
    } catch (error) {
      console.error("Error updating usage:", error);
      message.error("Failed to update usage allocation");
    } finally {
      setLoading(false);
    }
  };

  // Delete
  const handleDelete = async (usageId) => {
    setLoading(true);
    try {
      await deleteUsageRecords(id, [usageId]);
      message.success("Usage record deleted");
      fetchUsageHistory();
    } catch (error) {
      console.error("Error deleting usage:", error);
      message.error("Failed to delete usage record");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Plan",
      dataIndex: "subscriptionPlan",
      key: "subscriptionPlan",
      render: (plan) => {
        const colors = {
          Free: "green",
          Optimizer: "blue",
          Wizard: "purple",
          Maverick: "gold",
        };
        return <Tag color={colors[plan] || "default"}>{plan}</Tag>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "subscriptionStartDate",
      key: "subscriptionStartDate",
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a.subscriptionStartDate) - new Date(b.subscriptionStartDate),
    },
    {
      title: "End Date",
      dataIndex: "subscriptionEndDate",
      key: "subscriptionEndDate",
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a.subscriptionEndDate) - new Date(b.subscriptionEndDate),
      defaultSortOrder: "descend",
    },
    {
      title: "Credits Usage",
      key: "creditsUsage",
      render: (_, record) => {
        const allotted = record.creditsAllotted || 0;
        const used = record.creditsUsed || 0;
        const percentage = calculatePercentage(used, allotted);
        return (
          <div style={{ minWidth: 120 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              {used.toLocaleString()} / {allotted.toLocaleString()}
            </div>
            <Progress
              percent={percentage}
              size="small"
              status={percentage >= 90 ? "exception" : "active"}
            />
          </div>
        );
      },
    },
    {
      title: "Billing",
      dataIndex: "billInterval",
      key: "billInterval",
      render: (interval) =>
        interval === "month" ? "Monthly" : interval === "year" ? "Annual" : interval || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size={4}>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => showEditUsageModal(record._id)}
          />
          <Popconfirm
            title="Delete this usage record?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Usage History</h1>
          <p style={{ margin: 0, color: "#666" }}>
            View and manage usage allocations
          </p>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddUsageModal}
          >
            Add Usage
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()}>
            Go Back
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input
              placeholder="Search by plan"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              allowClear
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by plan"
              onChange={handlePlanFilterChange}
              value={planFilter}
              allowClear
            >
              <Option value="Free">Free</Option>
              <Option value="Optimizer">Optimizer</Option>
              <Option value="Wizard">Wizard</Option>
              <Option value="Maverick">Maverick</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={resetFilters}
              style={{ width: "100%" }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 80,
          }}
        >
          <Spin size="large" />
          <span style={{ marginTop: 16, color: "#666" }}>
            Loading usage history...
          </span>
        </div>
      ) : filteredHistory.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            dataSource={filteredHistory}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total) => `Total ${total} records`,
            }}
          />
        </Card>
      ) : (
        <Card>
          <Empty description="No usage history found">
            <Button type="primary" onClick={showAddUsageModal}>
              Add First Usage Record
            </Button>
          </Empty>
        </Card>
      )}

      {/* Add Usage Modal */}
      <Modal
        title="Add Usage Allocation"
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUsage}
          initialValues={{
            plan: "Free",
            dateRange: [moment(), moment().add(1, "month")],
            creditsAllotted: 1000,
            billInterval: "month",
          }}
        >
          <Form.Item
            name="plan"
            label="Subscription Plan"
            rules={[{ required: true, message: "Please select a plan" }]}
          >
            <Select>
              <Option value="Free">Free</Option>
              <Option value="Optimizer">Optimizer</Option>
              <Option value="Wizard">Wizard</Option>
              <Option value="Maverick">Maverick</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: "Please select a date range" }]}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="creditsAllotted"
            label="Credits Allotted"
            rules={[{ required: true, message: "Please enter credits" }]}
          >
            <Input type="number" min={0} />
          </Form.Item>

          <Form.Item
            name="billInterval"
            label="Billing Interval"
            rules={[{ required: true, message: "Please select billing interval" }]}
          >
            <Select>
              <Option value="month">Monthly</Option>
              <Option value="year">Annual</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setAddModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Usage
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Usage Modal */}
      <Modal
        title="Edit Usage Allocation"
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUsage}>
          <Form.Item
            name="plan"
            label="Subscription Plan"
            rules={[{ required: true, message: "Please select a plan" }]}
          >
            <Select>
              <Option value="Free">Free</Option>
              <Option value="Optimizer">Optimizer</Option>
              <Option value="Wizard">Wizard</Option>
              <Option value="Maverick">Maverick</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: "Please select a date range" }]}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="creditsAllotted"
            label="Credits Allotted"
            rules={[{ required: true, message: "Please enter credits allotted" }]}
          >
            <Input type="number" min={0} />
          </Form.Item>

          <Form.Item
            name="creditsUsed"
            label="Credits Used"
            rules={[{ required: true, message: "Please enter credits used" }]}
          >
            <Input type="number" min={0} />
          </Form.Item>

          <Form.Item
            name="billInterval"
            label="Billing Interval"
            rules={[{ required: true, message: "Please select billing interval" }]}
          >
            <Select>
              <Option value="month">Monthly</Option>
              <Option value="year">Annual</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Usage
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsageHistory;
